/* ------------------------------------------------------------------ */
/*  Overlay coordinate editing — shared model                           */
/* ------------------------------------------------------------------ */
/**
 * The coordinate model shared by the two surfaces that edit a drawing's anchor
 * point numerically: the settings drawer's Coordinates section and the quick
 * bar's coordinate popover. Kept in one place so the two never drift on which
 * overlays are editable, which fields each exposes, or how time is formatted.
 */

/** Overlays whose anchor VALUES are numerically editable (price and/or time). */
export const COORDINATE_EDITABLE = new Set<string>([
  'horizontalStraightLine',
  'horizontalRayLine',
  'verticalStraightLine',
])

/**
 * Which anchor fields an overlay exposes: horizontal lines edit price, vertical
 * lines edit time, a horizontal ray edits both.
 */
export function coordinateFields(name: string): { price: boolean; time: boolean } {
  return {
    price: name === 'horizontalStraightLine' || name === 'horizontalRayLine',
    time: name === 'verticalStraightLine' || name === 'horizontalRayLine',
  }
}

/** Coerce a klinecharts points array to fully-populated anchor points. */
export function normalizePoints(
  points: Array<{ timestamp?: number; value?: number }> | undefined,
): Array<{ timestamp: number; value: number }> {
  if (!points) return []
  return points.map((p) => ({ timestamp: p.timestamp ?? 0, value: p.value ?? 0 }))
}

/**
 * Rebuild a points array from an anchor patch, preserving every point past the
 * anchor (e.g. a ray's direction point) untouched. Returns the new array so the
 * caller can push it live via `overrideOverlayPoints`.
 */
export function patchAnchorPoints(
  points: Array<{ timestamp: number; value: number }>,
  patch: { timestamp?: number; value?: number },
): Array<{ timestamp: number; value: number }> {
  const anchor = points[0] ?? { timestamp: 0, value: 0 }
  const nextAnchor = {
    timestamp: patch.timestamp ?? anchor.timestamp,
    value: patch.value ?? anchor.value,
  }
  return [nextAnchor, ...points.slice(1)]
}

/** Epoch ms → `YYYY-MM-DDTHH:mm` for a native datetime-local input (local tz). */
export function tsToLocalInput(ts: number): string {
  if (!Number.isFinite(ts)) return ''
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** datetime-local value → epoch ms (null when unparseable). */
export function localInputToTs(v: string): number | null {
  if (!v) return null
  const ms = new Date(v).getTime()
  return Number.isFinite(ms) ? ms : null
}
